import React, { useCallback, useEffect } from 'react';
import { Alert, Modal } from 'react-bootstrap';
import { ClinicalButton, ClinicalModalActions } from './ui';
import { formatDateFr } from '../../services/consultationRestDayService';
import './ConsultationRestDaysViewModal.css';

const LOGO_SRC = `${process.env.PUBLIC_URL || ''}/logo.png`;

export default function ConsultationRestDaysViewModal({
  show,
  onHide,
  consultation,
  restDay,
  previewUnsaved = false
}) {
  const runPrint = useCallback(() => {
    document.body.classList.add('rest-days-print-mode');
    const cleanup = () => {
      document.body.classList.remove('rest-days-print-mode');
      window.removeEventListener('afterprint', cleanup);
    };
    window.addEventListener('afterprint', cleanup);
    window.setTimeout(() => window.print(), 150);
  }, []);

  useEffect(() => {
    if (!show) document.body.classList.remove('rest-days-print-mode');
  }, [show]);

  const patientName = (consultation?.patient_name || '').trim() || '—';
  const doctorName = (consultation?.doctor_name || '').trim() || '—';
  const daysCount = restDay?.days_count || 0;

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      scrollable
      className="rest-days-view-modal clinical-view-modal"
    >
      <Modal.Header closeButton className="rest-days-view-modal__head recv-no-print">
        <div className="d-flex align-items-center gap-2">
          <img src={LOGO_SRC} alt="" className="rest-days-view-modal__head-logo" />
          <Modal.Title className="fs-6 mb-0">Certificat — jours de repos</Modal.Title>
        </div>
      </Modal.Header>

      <Modal.Body className="rest-days-view-modal__body">
        <ClinicalModalActions>
          <ClinicalButton variant="secondary" size="sm" onClick={runPrint}>
            Imprimer
          </ClinicalButton>
          <ClinicalButton variant="primary" size="sm" icon="download" onClick={runPrint}>
            PDF
          </ClinicalButton>
        </ClinicalModalActions>

        {previewUnsaved ? (
          <Alert variant="warning" className="py-2 small recv-no-print">
            Aperçu non enregistré — enregistrez le certificat avant impression officielle.
          </Alert>
        ) : null}

        <article className="rd-cert" id="rest-days-print-sheet">
          <header className="rd-cert__header">
            <img src={LOGO_SRC} alt="FUNTOA SMIE" className="rd-cert__logo" />
            <div>
              <h1 className="rd-cert__brand">FUNTOA SMIE</h1>
              <p className="rd-cert__doctype">Certificat médical de jours de repos</p>
            </div>
          </header>

          <p className="rd-cert__meta">
            Consultation n° <strong>{consultation?.id ?? '—'}</strong>
            {' · '}
            édité le {new Date().toLocaleString('fr-FR', { dateStyle: 'long', timeStyle: 'short' })}
          </p>

          <div className="rd-cert__info">
            <p>
              <strong>Patient :</strong> {patientName}
              {consultation?.patient_age != null ? `, ${consultation.patient_age} ans` : ''}
            </p>
            {consultation?.patient_phone ? (
              <p>
                <strong>Téléphone :</strong> {consultation.patient_phone}
              </p>
            ) : null}
          </div>

          <div className="rd-cert__period">
            <p className="rd-cert__period-label">Période d&apos;arrêt de travail</p>
            <p className="rd-cert__period-value">
              <strong>{daysCount}</strong> jour{daysCount > 1 ? 's' : ''} de repos
            </p>
            <p className="rd-cert__period-dates">
              Du <strong>{formatDateFr(restDay?.start_date)}</strong>
              {' au '}
              <strong>{formatDateFr(restDay?.end_date)}</strong>
              {' inclus.'}
            </p>
          </div>

          <div className="rd-cert__block">
            <p className="rd-cert__block-label">Motif médical</p>
            <p className="rd-cert__block-text">{(restDay?.clinical_motif || '').trim() || '—'}</p>
          </div>

          {(restDay?.recommendations || '').trim() ? (
            <div className="rd-cert__block">
              <p className="rd-cert__block-label">Recommandations</p>
              <p className="rd-cert__block-text">{restDay.recommendations.trim()}</p>
            </div>
          ) : null}

          <div className="rd-cert__sign">
            <p>Signature et cachet du médecin</p>
            <div className="rd-cert__sign-line">&nbsp;</div>
          </div>

          <p className="rd-cert__foot">
            FUNTOA SMIE — document à remettre au patient et, le cas échéant, à l&apos;employeur.
          </p>
          {doctorName !== '—' ? (
            <p className="rd-cert__doctor-quiet">Médecin prescripteur : {doctorName}</p>
          ) : null}
        </article>
      </Modal.Body>

      <Modal.Footer className="recv-no-print">
        <ClinicalButton variant="ghost" size="sm" onClick={onHide}>
          Fermer
        </ClinicalButton>
        <ClinicalButton variant="primary" size="sm" icon="download" onClick={runPrint}>
          Exporter en PDF
        </ClinicalButton>
      </Modal.Footer>
    </Modal>
  );
}
